import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, TextField, InputAdornment,
  MenuItem, LinearProgress, Tooltip, Card, CardContent, CardActions,
  Stack, useTheme, useMediaQuery, IconButton, TablePagination,
} from '@mui/material';
import { Add, Search, LockOpen, Lock } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { projectService } from '@smartgrow/shared';

const getStatusColor = (status: string): 'success' | 'primary' | 'warning' | 'default' => {
  switch (status) {
    case 'ACTIVE': case 'IN_PROGRESS': return 'success';
    case 'COMPLETED': return 'primary';
    case 'PLANNING': case 'ON_HOLD': case 'PAUSED': return 'warning';
    default: return 'default';
  }
};

const getBudgetColor = (pct: number): 'error' | 'warning' | 'success' => {
  if (pct >= 90) return 'error';
  if (pct >= 70) return 'warning';
  return 'success';
};

const fmt = (n: number) => `₦${n.toLocaleString('en-NG')}`;

export const ProjectsPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filter, setFilter] = useState('ALL');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchQuery); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const closeMutation = useMutation({
    mutationFn: (id: string) => projectService.closeProject(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });

  const reopenMutation = useMutation({
    mutationFn: (id: string) => projectService.reopenProject(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });

  const { data } = useQuery({
    queryKey: ['projects', filter, page, debouncedSearch],
    queryFn: () => projectService.search({
      page, size: 10,
      search: debouncedSearch || undefined,
      active: filter === 'ALL' ? undefined : filter === 'ACTIVE',
    }),
  });

  const filteredProjects = data?.content ?? [];

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold">Projects</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/dashboard/projects/new')} size={isMobile ? 'small' : 'medium'}>
          New Project
        </Button>
      </Box>

      <Paper sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField placeholder="Search projects..." value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)} size="small"
            sx={{ flex: 1, minWidth: 180 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
          <TextField select value={filter} onChange={(e) => { setFilter(e.target.value); setPage(0); }}
            size="small" sx={{ minWidth: 140 }}>
            <MenuItem value="ALL">All Projects</MenuItem>
            <MenuItem value="ACTIVE">Active</MenuItem>
            <MenuItem value="COMPLETED">Completed</MenuItem>
          </TextField>
        </Box>
      </Paper>

      {isMobile ? (
        <Stack spacing={2}>
          {filteredProjects.length === 0 && (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>No projects found</Typography>
          )}
          {filteredProjects.map((project) => {
            const budget = project.budget ?? 0;
            const spent = (project as any).totalSpent ?? 0;
            const remaining = (project as any).remainingBudget ?? (budget - spent);
            const pct = budget > 0 ? Math.min(100, Math.round((spent / budget) * 100)) : 0;
            const barColor = getBudgetColor(pct);
            return (
              <Card key={project.id} variant="outlined" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/projects/${project.id}`)}>
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" fontWeight="medium">{project.projectName || project.name}</Typography>
                    <Chip label={project.status} color={getStatusColor(project.status)} size="small" />
                  </Box>
                  <Typography variant="caption" fontFamily="monospace" color="text.secondary">{project.projectCode}</Typography>
                  {project.managerName && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Manager: {project.managerName}</Typography>
                  )}
                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption" color="text.secondary">Budget: {fmt(budget)}</Typography>
                      <Typography variant="caption" color={barColor + '.main'}>{pct}% used</Typography>
                    </Box>
                    <LinearProgress variant="determinate" value={pct} color={barColor}
                      sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
                    <Typography variant="caption" color={remaining < 0 ? 'error.main' : 'success.main'}>
                      Remaining: {remaining < 0 ? '-' : ''}{fmt(Math.abs(remaining))}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ pt: 0 }}>
                  <Button size="small" onClick={() => navigate(`/dashboard/projects/${project.id}`)}>View</Button>
                </CardActions>
              </Card>
            );
          })}
        </Stack>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Code</TableCell>
                <TableCell>Manager</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Budget</TableCell>
                <TableCell>Spent</TableCell>
                <TableCell>Remaining</TableCell>
                <TableCell sx={{ minWidth: 140 }}>Budget Used</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredProjects.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>No projects found</Typography>
                  </TableCell>
                </TableRow>
              ) : filteredProjects.map((project, i) => {
                const budget = project.budget ?? 0;
                const spent = project.totalSpent ?? 0;
                const remaining = project.remainingBudget ?? (budget - spent);
                const pct = budget > 0 ? Math.min(100, Math.round((spent / budget) * 100)) : 0;
                const barColor = getBudgetColor(pct);
                return (
                  <TableRow key={project.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/dashboard/projects/${project.id}`)}>
                    <TableCell>{page * 10 + i + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">{project.projectName}</Typography>
                      {project.description && (
                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', maxWidth: 200 }}>
                          {project.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">{project.projectCode}</Typography>
                    </TableCell>
                    <TableCell>{project.managerName ?? '—'}</TableCell>
                    <TableCell>
                      <Chip label={project.status} color={getStatusColor(project.status)} size="small" />
                    </TableCell>
                    <TableCell><Typography variant="body2" fontWeight="bold">{fmt(budget)}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2" color={pct >= 90 ? 'error.main' : 'text.primary'}>{fmt(spent)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color={remaining < 0 ? 'error.main' : 'success.main'} fontWeight="medium">
                        {remaining < 0 ? '-' : ''}{fmt(Math.abs(remaining))}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <Tooltip title={`${pct}% used`}>
                        <Box>
                          <Typography variant="caption" color={barColor + '.main'}>{pct}%</Typography>
                          <LinearProgress variant="determinate" value={pct} color={barColor}
                            sx={{ height: 6, borderRadius: 3, mt: 0.5 }} />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{new Date(project.startDate).toLocaleDateString()}</TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Button size="small" onClick={() => navigate(`/dashboard/projects/${project.id}`)}>View</Button>
                        {project.status === 'ACTIVE' ? (
                          <Tooltip title="Close project">
                            <IconButton size="small" color="warning" onClick={() => { if (globalThis.confirm(`Close project "${project.projectName}"?`)) closeMutation.mutate(project.id); }}>
                              <Lock fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Reopen project">
                            <IconButton size="small" color="success" onClick={() => reopenMutation.mutate(project.id)}>
                              <LockOpen fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={data?.totalElements ?? 0}
            page={page}
            onPageChange={(_e, newPage) => setPage(newPage)}
            rowsPerPage={10}
            rowsPerPageOptions={[10]}
          />
        </TableContainer>
      )}
    </Box>
  );
};
